// capabilities.ts — role ∩ entitlement gating (TD-030).
//
// This map MIRRORS thin-router/routers/townforge_auth.py::POLICY. This copy drives nav visibility
// and route guards, which is a USABILITY feature. The backend copy is the security boundary — a
// hidden nav item is still a reachable URL.
//
// Keeping two copies is deliberate: the frontend needs the policy to render, and the backend must
// never trust the frontend. The capability catalog test asserts they agree, so drift fails a test
// rather than silently locking out a paying tenant or unlocking a premium surface.
//
// DEFAULT-OFF CONTRACT, AND ITS LIMIT: the passthrough exists so the app renders in local
// development with NO backend at all. It applies only when there is genuinely no identity to gate
// on — `authEnabled === false` AND no role.
//
// Once /me returns a real role, gating is honoured even while authentication is bypassed. That
// distinction matters: with the bypass on and a seeded role, the old passthrough handed every
// portal to everyone, so an Arrears Manager could open the tender system in the UI. The server
// refused the calls, but the navigation was a lie about what that person may do — and a demo run
// that way teaches the wrong thing about the product.

export type RoleKind =
  | 'tc_admin' | 'procurement_officer' | 'project_manager' | 'evaluator'
  | 'approving_officer' | 'tc_viewer'
  | 'tenderer_admin' | 'tenderer_preparer' | 'tenderer_signatory'
  | 'ma_admin' | 'ma_lead' | 'ma_member'
  | 'internal_auditor' | 'external_auditor'
  // Arrears Management. These are Town Council roles, so ORG TYPE ALONE CANNOT DECIDE THE PORTAL
  // any more — see portalsFor. An Arrears Manager works in a Town Council and must never be shown
  // the tender system.
  | 'arrears_manager' | 'arrears_officer' | 'property_officer' | 'clt_officer'
  | 'call_centre_officer'
  | 'property_manager' | 'finance_admin_manager'

export type OrgType =
  | 'town_council' | 'managing_agent' | 'tenderer' | 'auditor' | 'regulator' | 'platform'

export type PortalKey = 'tc' | 'ams' | 'bid' | 'ma' | 'audit' | 'dev'

interface Rule {
  roles?: RoleKind[]
  feature?: string
  /**
   * Reserved to the Town Council. NO tender grant extends a reserved capability to a Managing
   * Agent (workflow-state-model.md §10) — an agent may produce the decision, never be it.
   */
  reserved?: boolean
}

export const POLICY: Record<string, Rule> = {
  'tender.author':         { roles: ['procurement_officer', 'tc_admin', 'ma_lead'] },
  'tender.publish':        { roles: ['procurement_officer', 'tc_admin'], reserved: true },
  'tender.open':           { roles: ['procurement_officer', 'tc_admin'], reserved: true },
  'tender.responsiveness': { roles: ['procurement_officer', 'project_manager', 'tc_admin'], reserved: true },
  'eval.manage':           { roles: ['project_manager', 'ma_lead'] },
  'eval.score':            { roles: ['evaluator', 'project_manager', 'ma_member', 'ma_lead'] },
  'eval.ai_firstcut':      { roles: ['project_manager', 'ma_lead'], feature: 'ai_evaluation' },
  'recommendation.write':  { roles: ['project_manager', 'ma_lead'] },
  'approval.act':          { roles: ['approving_officer', 'tc_admin'], reserved: true },
  'award.snapshot':        { roles: ['approving_officer', 'tc_admin'], reserved: true },
  'supplier.performance':  { roles: ['tc_admin', 'project_manager'], reserved: true },
  'grant.manage':          { roles: ['tc_admin'], reserved: true },
  'sign.envelope.create':  { roles: ['procurement_officer', 'project_manager', 'tc_admin', 'ma_lead'] },
  'bid.prepare':           { roles: ['tenderer_preparer', 'tenderer_admin'] },
  'bid.submit':            { roles: ['tenderer_signatory'] },
  'bid.advisor':           { roles: ['tenderer_preparer', 'tenderer_admin'], feature: 'forgebid' },
  'audit.review':          { roles: ['internal_auditor', 'external_auditor'] },

  // Arrears Management. `arrears.approve_summons` is separate from `arrears.approve` because the
  // operating model separates them: a Property Manager approves demands, the Finance & Admin
  // Manager signs off a summons. One capability for both would let a Property Manager start court
  // action.
  'arrears.view':            { roles: ['arrears_manager', 'arrears_officer', 'property_officer',
                                       'clt_officer', 'property_manager', 'call_centre_officer',
                                       'finance_admin_manager', 'tc_admin'] },
  'arrears.case.manage':     { roles: ['arrears_manager', 'arrears_officer', 'clt_officer',
                                       'tc_admin'] },
  // Recording that a conversation happened is NOT case management. A call centre agent answers the
  // phone, writes down what the resident said and what was agreed, and changes nothing else: no
  // category, no suppression, no letter. Folding this into arrears.case.manage would have handed
  // an agent the power to reclassify a resident as hardship from a phone call.
  'arrears.contact.log':     { roles: ['call_centre_officer', 'arrears_officer', 'arrears_manager',
                                       'property_officer', 'clt_officer', 'tc_admin'] },
  'arrears.recommend':       { roles: ['arrears_manager', 'arrears_officer'] },
  'arrears.approve':         { roles: ['property_manager', 'tc_admin'], reserved: true },
  'arrears.approve_summons': { roles: ['finance_admin_manager', 'tc_admin'], reserved: true },
  'arrears.visit':           { roles: ['property_officer', 'clt_officer', 'arrears_manager',
                                       'tc_admin'] },
  // ── The hardship boundary (mockup v2) ───────────────────────────────────────────────────────
  // A Property Officer collects and confirms. When they SEE hardship they hand the case to the
  // Community Liaison Team; they do not classify it. That is not a UI preference — Category 1
  // suppresses letters and fees under a named rule, and the assessment behind it needs household
  // circumstances, evidence and consent that a doorstep collection visit does not gather.
  //
  // So classifying into 1A–1F is its own capability and the Property Officer does NOT hold it.
  // 'arrears.case.manage' still covers Categories 2–4, which is the PO's actual job.
  'arrears.hardship.assess': { roles: ['clt_officer', 'arrears_manager', 'tc_admin'] },
  // The call centre's ranked work list. Separate from arrears.view because seeing a case and being
  // handed a queue of people to ring today are different entitlements.
  'arrears.contact.queue':   { roles: ['call_centre_officer', 'arrears_officer', 'arrears_manager',
                                       'tc_admin'] },
  // Looking a resident up by name, block or account. Every arrears role needs it — a call centre
  // agent with someone on the line cannot navigate a work queue to find them.
  'arrears.inquiry':         { roles: ['call_centre_officer', 'arrears_officer', 'arrears_manager',
                                       'property_officer', 'clt_officer', 'property_manager',
                                       'finance_admin_manager', 'tc_admin'] },
  // Screens that belong to a team, not to everyone with a login. A call centre agent has no
  // business in the approval chain or the legal ladder, and a Property Officer walking a round
  // does not configure outreach campaigns. Separated from 'arrears.view' because SEEING A CASE and
  // BEING PART OF A PROCESS are different entitlements — a role should see its own app, not the
  // whole app with most of it refusing them.
  'arrears.approvals.view':  { roles: ['arrears_officer', 'arrears_manager', 'property_manager',
                                       'finance_admin_manager', 'tc_admin'] },
  'arrears.legal.view':      { roles: ['arrears_officer', 'arrears_manager', 'property_manager',
                                       'finance_admin_manager', 'tc_admin'] },
  'arrears.reports.view':    { roles: ['arrears_manager', 'property_manager',
                                       'finance_admin_manager', 'tc_admin'] },
  'arrears.rules.manage':    { roles: ['tc_admin'], reserved: true },
}

/** Roles whose whole job is arrears. They see the AMS portal and nothing else. */
const ARREARS_ONLY_ROLES: RoleKind[] = [
  'arrears_manager', 'arrears_officer', 'property_officer', 'clt_officer',
  'property_manager', 'finance_admin_manager', 'call_centre_officer',
]

export interface Identity {
  role: RoleKind | null
  orgType: OrgType | null
  features: string[]
  isSuperuser: boolean
  authEnabled: boolean
}

export type DenyReason = 'role' | 'feature' | 'reserved' | 'no_identity'

/**
 * Returns WHY a capability is denied, not just whether. The distinction drives the UI: a
 * role-denied item vanishes, a licence-denied one stays visible and locked so the commercial
 * offer is discoverable rather than merely enforced.
 */
export function canDetail(id: Identity, cap: string): { ok: true } | { ok: false; why: DenyReason } {
  // Passthrough ONLY when there is no identity at all to gate on. See the header.
  if (!id.authEnabled && !id.role) return { ok: true }
  if (id.isSuperuser) return { ok: true }
  const rule = POLICY[cap]
  if (!rule) return { ok: false, why: 'role' }      // unknown capability fails closed
  if (!id.role || !id.orgType) return { ok: false, why: 'no_identity' }
  if (rule.reserved && id.orgType !== 'town_council') return { ok: false, why: 'reserved' }
  if (rule.roles && !rule.roles.includes(id.role)) return { ok: false, why: 'role' }
  if (rule.feature && !id.features.includes(rule.feature)) return { ok: false, why: 'feature' }
  return { ok: true }
}

export function can(id: Identity, cap: string): boolean {
  return canDetail(id, cap).ok
}

/**
 * Which portals this identity may enter. Enforced again by a route guard on each portal shell.
 *
 * ORG TYPE IS NO LONGER SUFFICIENT. A Town Council now runs two systems, and most of its staff
 * belong to exactly one of them: an Arrears Manager has no business in the tender system, and a
 * Procurement Officer has none in arrears. So the ROLE decides, with the org type as the fallback
 * for roles that predate the split.
 *
 * `tc_admin` is the deliberate exception — it administers the whole Town Council and sees both.
 */
export function portalsFor(id: Identity): PortalKey[] {
  const ALL: PortalKey[] = ['tc', 'ams', 'bid', 'ma', 'audit', 'dev']
  if (!id.authEnabled && !id.role) return ALL
  if (id.isSuperuser) return ALL
  if (id.role && ARREARS_ONLY_ROLES.includes(id.role)) return ['ams']
  switch (id.orgType) {
    case 'town_council':   return id.role === 'tc_admin' ? ['tc', 'ams'] : ['tc']
    case 'tenderer':       return ['bid']
    case 'managing_agent': return ['ma']
    case 'auditor':        return ['audit']
    default:               return []
  }
}
